import { useEffect, useMemo, useRef, useState } from "react";
import { mastersApi, type OpeningsSheet } from "../lib/api";
import { useCompany } from "./reference";
import { useLoad, errText } from "../ui/useLoad";
import { fmtAmount, fmtSigned, parseAmount, type Side } from "../lib/money";
import { fuzzyFilter } from "../lib/fuzzy";
import { ErrorNote, Loading, Notice, PageHead } from "../ui/bits";
import { NATURE } from "./groups";
import type { Nature } from "../lib/api";

interface Row { side: Side; amount: string }

// Every ledger's opening balance for the opening year on one sheet. Enter / ↓ moves down, ↑ up, Ctrl+S saves the
// changed rows; the difference (Dr − Cr) shows live.
export function Openings() {
  const { company, ref } = useCompany();
  const { data, error, loading, reload } = useLoad(() => mastersApi.openings(company.id), [company.id]);
  const [edits, setEdits] = useState<Record<string, Row>>({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const table = useRef<HTMLTableElement>(null);
  const canEdit = (ref?.can_edit ?? false) && !data?.closed_reason;

  const rowOf = (l: OpeningsSheet["ledgers"][number]): Row =>
    edits[l.id] ?? { side: l.side ?? (l.nature === "L" || l.nature === "I" ? "Cr" : "Dr"), amount: l.amount != null ? fmtAmount(l.amount) : "" };

  const ledgers = useMemo(() => {
    const all = (data?.ledgers ?? []).filter((l) => l.active || l.amount);
    return q.trim() ? fuzzyFilter(all, q, (l) => `${l.name} ${l.group_name}`, 500) : all;
  }, [data, q]);

  const totals = useMemo(() => {
    let dr = 0, cr = 0, bad = 0;
    for (const l of data?.ledgers ?? []) {
      const r = rowOf(l);
      const n = parseAmount(r.amount);
      if (n == null) continue;
      if (Number.isNaN(n)) { bad++; continue; }
      if (r.side === "Dr") dr += n; else cr += n;
    }
    return { dr, cr, diff: Math.round((dr - cr) * 100) / 100, bad };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, edits]);
  const changed = Object.keys(edits).length;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!data) return <section className="page"><PageHead crumb="Accounting" title="Opening balances" />{loading ? <Loading /> : <ErrorNote text={error?.message} />}</section>;

  const edit = (id: string, r: Row) => { setEdits({ ...edits, [id]: r }); setMsg(null); };

  async function save() {
    if (!changed || busy || !data) return;
    if (totals.bad) { setErr("Some amounts aren't valid (rupees and paise, e.g. 1,500.50)."); return; }
    setBusy(true); setErr(null);
    try {
      const rows = Object.entries(edits).map(([id, r]) => ({ ledger_id: id, side: r.side, amount: parseAmount(r.amount) }));
      const diff = await mastersApi.setOpenings(company.id, rows);
      setEdits({});
      await reload();
      setMsg(`Saved ${rows.length} balance${rows.length > 1 ? "s" : ""}. Difference: ${fmtSigned(diff)}.`);
    } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  }

  const move = (from: HTMLElement, dir: 1 | -1) => {
    const ins = Array.from(table.current?.querySelectorAll<HTMLInputElement>("input.amount") ?? []);
    const i = ins.indexOf(from as HTMLInputElement);
    const next = ins[i + dir];
    if (next) { next.focus(); next.select(); }
  };

  let lastNature: Nature | null = null;
  return (
    <section className="page">
      <PageHead crumb="Accounting" title={`Opening balances — ${data.year.label}`}>
        {canEdit && <button className="primary" onClick={save} disabled={!changed || busy}>{busy ? "Saving…" : `Save${changed ? ` (${changed})` : ""}`} <kbd>Ctrl+S</kbd></button>}
      </PageHead>
      {data.closed_reason && <Notice>Opening balances can't be changed: {data.closed_reason}.</Notice>}
      <div className="toolbar">
        <input placeholder="Filter accounts" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter accounts" />
        <span className="muted small">As at {new Date(company.books_begin + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}, when the books begin.</span>
      </div>
      <ErrorNote text={err} />
      {msg && <p className="msg ok" role="status">{msg}</p>}
      <table className="grid-table openings" ref={table}>
        <thead><tr><th>Account</th><th>Under</th><th className="num">Amount</th><th>Dr / Cr</th></tr></thead>
        <tbody>
          {ledgers.map((l) => {
            const r = rowOf(l);
            const head = !q && l.nature !== lastNature ? <tr className="band" key={`h${l.nature}`}><td colSpan={4}>{NATURE[l.nature]}</td></tr> : null;
            lastNature = l.nature;
            const bad = Number.isNaN(parseAmount(r.amount));
            return [head, (
              <tr key={l.id} className={edits[l.id] ? "changed" : ""}>
                <td>{l.name}{l.method === "carried_forward" && <span className="tag">carried forward</span>}</td>
                <td className="muted">{l.group_name}</td>
                <td className="num"><input className={`amount num ${bad ? "invalid" : ""}`} inputMode="decimal" value={r.amount} disabled={!canEdit}
                  aria-label={`${l.name} amount`} placeholder="—"
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => edit(l.id, { ...r, amount: e.target.value })}
                  onBlur={(e) => { const n = parseAmount(e.target.value); if (n != null && !Number.isNaN(n) && edits[l.id]) edit(l.id, { ...r, amount: fmtAmount(n) }); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "ArrowDown") { e.preventDefault(); move(e.currentTarget, 1); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); move(e.currentTarget, -1); }
                    else if (e.key.toLowerCase() === "d" && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); edit(l.id, { ...r, side: "Dr" }); }
                    else if (e.key.toLowerCase() === "c" && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); edit(l.id, { ...r, side: "Cr" }); }
                  }} /></td>
                <td><select value={r.side} disabled={!canEdit} tabIndex={-1} aria-label={`${l.name} Dr or Cr`}
                  onChange={(e) => edit(l.id, { ...r, side: e.target.value as Side })}><option>Dr</option><option>Cr</option></select></td>
              </tr>
            )];
          })}
        </tbody>
        <tfoot>
          <tr><td colSpan={2}>Total</td><td className="num">{fmtAmount(totals.dr)} Dr</td><td className="num">{fmtAmount(totals.cr)} Cr</td></tr>
          <tr className={totals.diff ? "warn" : ""}><td colSpan={2}>Difference in opening balances</td><td className="num" colSpan={2}>{fmtSigned(totals.diff)}</td></tr>
        </tfoot>
      </table>
      {canEdit && <p className="muted small">Type an amount, then D or C to set the side. Enter moves down; Ctrl+S saves.</p>}
    </section>
  );
}
